import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { BookOpenCheck, Check, Download, Globe, Layers, RotateCcw, ShieldCheck, SlidersHorizontal } from 'lucide-react';
import type { AiPreferences, Mailbox } from '@mailpilot/shared';
import { api, downloadFile, type ResOf } from '../../lib/api';
import { qk } from '../../lib/keys';
import { Button, Card, CardBody, CardDivider, CardHeader, ErrorState, Notice, Skeleton, useConfirm, useToast } from '../../components/ui';
import { DangerCard, Rows, SettingRow } from '../../components/settings/SettingsPrimitives';
import { useMailboxes } from '../../hooks/useMailboxes';
import { PrefsForm, emptyPreferences } from '../../components/settings/PrefsForm';
import { PrefsExamples } from '../../components/settings/PrefsExamples';
import { SettingsTitle } from './SettingsLayout';

type PrefsRes = ResOf<'GET /api/ai/preferences'>;
const GLOBAL = 'global';

function ScopePicker({ value, onChange, mailboxes, configured }: { value: string; onChange: (v: string) => void; mailboxes: Mailbox[]; configured: Set<string> }) {
  const options = [
    { id: GLOBAL, title: 'Global', sub: 'Baseline for every account', color: null as string | null },
    ...mailboxes.map((m) => ({ id: m.id, title: m.displayName || m.email, sub: m.email, color: m.color })),
  ];
  return (
    <div role="radiogroup" aria-label="Preference scope" className="grid grid-cols-1 gap-2.5 min-[480px]:grid-cols-2 lg:grid-cols-3">
      {options.map((o) => {
        const active = o.id === value;
        const own = o.id === GLOBAL || configured.has(o.id);
        return (
          <button
            key={o.id}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(o.id)}
            className={clsx(
              'flex min-w-0 items-center gap-3 rounded-2xl px-3.5 py-3 text-left ring-1 transition-[background-color,box-shadow] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
              active ? 'bg-primary-soft ring-2 ring-primary' : 'bg-raised/80 ring-line/70 hover:bg-raised hover:ring-line',
            )}
          >
            <span className={clsx('flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px]', active ? 'bg-surface text-primary-ink shadow-sm' : 'bg-surface text-muted')}>
              {o.color ? <span className="h-3 w-3 rounded-full" style={{ background: o.color }} aria-hidden /> : <Globe className="h-4 w-4" aria-hidden />}
            </span>
            <span className="min-w-0 flex-1">
              <span className={clsx('block truncate text-[0.8125rem] font-medium', active ? 'text-primary-ink' : 'text-ink')}>{o.title}</span>
              <span className={clsx('block truncate text-xs', active ? 'text-primary-ink/70' : 'text-muted')}>{own ? o.sub : 'Uses global preferences'}</span>
            </span>
            {active && <Check className="h-4 w-4 shrink-0 text-primary-ink" aria-hidden />}
          </button>
        );
      })}
    </div>
  );
}

export function PreferencesSettings() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const prefsQ = useQuery({ queryKey: qk.aiPreferences, queryFn: () => api('GET /api/ai/preferences') });
  const mailboxesQ = useMailboxes();
  const mailboxes = useMemo(() => mailboxesQ.data?.mailboxes ?? [], [mailboxesQ.data]);
  const [scope, setScope] = useState<string>(GLOBAL);
  const [exporting, setExporting] = useState(false);

  const mailboxId = scope === GLOBAL ? null : scope;
  const scopeMailbox = mailboxId ? mailboxes.find((m) => m.id === mailboxId) ?? null : null;
  const scopeLabel = scopeMailbox ? scopeMailbox.displayName || scopeMailbox.email : 'global';

  const preferences = prefsQ.data?.preferences ?? [];
  const record = preferences.find((p) => (mailboxId ? p.scope === 'mailbox' && p.mailboxId === mailboxId : p.scope === 'global')) ?? null;
  const initial: AiPreferences = record ?? emptyPreferences(mailboxId);
  const configured = useMemo(() => new Set(preferences.filter((p) => p.scope === 'mailbox' && p.mailboxId).map((p) => p.mailboxId as string)), [preferences]);
  const examples = (prefsQ.data?.examples ?? []).filter((e) => e.mailboxId === mailboxId);

  const save = useMutation({
    mutationFn: (p: AiPreferences) => api('PUT /api/ai/preferences', { body: { ...p, scope: mailboxId ? 'mailbox' : 'global', mailboxId } }),
    onSuccess: (res) => {
      qc.setQueryData<PrefsRes>(qk.aiPreferences, (prev) => {
        if (!prev) return prev;
        const saved = res.preferences;
        const rest = prev.preferences.filter((p) => !(p.scope === saved.scope && p.mailboxId === saved.mailboxId));
        return { ...prev, preferences: [...rest, saved] };
      });
      toast.success('Preferences saved');
    },
    onError: (e) => toast.error(e),
  });

  const reset = useMutation({
    mutationFn: (body: { mailboxId?: string | null }) => api('POST /api/ai/learned/reset', { body }),
    onSuccess: () => {
      toast.success('Learned data reset');
      void qc.invalidateQueries({ queryKey: qk.aiPreferences });
    },
    onError: (e) => toast.error(e),
  });

  const askReset = async (all: boolean) => {
    const ok = await confirm({
      title: all ? 'Reset everything the assistant has learned?' : `Reset ${scopeMailbox ? `“${scopeLabel}”` : 'global'} learned data?`,
      message: all
        ? 'This deletes the preferences and every learned example and correction for all scopes. It cannot be undone — export first if you may want them back.'
        : `This deletes the ${scopeMailbox ? 'preferences, examples and corrections for this account' : 'global preferences and examples'}. It cannot be undone.`,
      confirmLabel: all ? 'Reset everything' : 'Reset',
      danger: true,
    });
    if (ok) reset.mutate(all ? {} : { mailboxId });
  };

  const exportLearned = async () => {
    setExporting(true);
    try {
      await downloadFile('/api/ai/learned/export', 'mailpilot-learned.json');
    } catch (e) {
      toast.error(e);
    } finally {
      setExporting(false);
    }
  };

  const title = (
    <SettingsTitle title="AI preferences" description="Teach the assistant how you write. Preferences and examples are kept per scope so personal and business styles never mix." />
  );

  if (prefsQ.isLoading) {
    return (
      <div>
        {title}
        <div className="space-y-6" aria-busy="true">
          <Skeleton className="h-16 w-full rounded-2xl" />
          <Skeleton className="h-44 w-full rounded-panel" />
          <Skeleton className="h-96 w-full rounded-panel" />
        </div>
      </div>
    );
  }
  if (prefsQ.error) {
    return (
      <div>
        {title}
        <Card>
          <ErrorState error={prefsQ.error} onRetry={() => void prefsQ.refetch()} />
        </Card>
      </div>
    );
  }

  return (
    <div>
      {title}
      <div className="space-y-6">
        <Notice tone="info" icon={<ShieldCheck className="h-4 w-4" aria-hidden />}>
          <strong className="font-semibold">Retrieval and explicit preferences, not model fine-tuning.</strong> Your mailbox content is never used to train a general model. You can inspect, edit, delete, export or reset everything here.
        </Notice>

        <Card>
          <CardHeader
            icon={<Layers className="h-5 w-5" />}
            title="Scope"
            description="Global preferences apply to every account. A mailbox with its own preferences overrides the global ones and keeps separate examples."
          />
          <CardBody>
            {mailboxesQ.isLoading ? <Skeleton className="h-16 w-full rounded-2xl" /> : <ScopePicker value={scope} onChange={setScope} mailboxes={mailboxes} configured={configured} />}
            {mailboxesQ.error ? <p className="mt-3 text-xs text-danger-ink">Couldn't load your accounts — only global preferences are shown.</p> : null}
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            icon={<SlidersHorizontal className="h-5 w-5" />}
            title={scopeMailbox ? `Preferences for ${scopeLabel}` : 'Global preferences'}
            description={
              scopeMailbox
                ? record
                  ? `Used for drafts from ${scopeMailbox.email}. Empty fields fall back to global preferences.`
                  : `${scopeMailbox.email} currently uses the global preferences. Saving here creates a separate style for this account.`
                : 'The baseline for every account without its own preferences.'
            }
          />
          <CardBody>
            <PrefsForm key={`${scope}:${JSON.stringify(record)}`} initial={initial} onSave={(p) => save.mutate(p)} saving={save.isPending} scopeLabel={scopeMailbox ? 'account' : 'global'} />
          </CardBody>
        </Card>

        <PrefsExamples examples={examples} mailboxId={mailboxId} scopeLabel={scopeMailbox ? scopeLabel : null} />

        <Card>
          <CardHeader icon={<Download className="h-5 w-5" />} title="Your learned data" description="Download or clear what the assistant uses to personalise drafts." />
          <CardDivider className="mt-5" />
          <CardBody className="!py-5">
            <Rows>
              <SettingRow
                label="Export learned data"
                description="A JSON file with every preference, example and correction across all scopes."
                control={
                  <Button size="sm" icon={<Download className="h-4 w-4" aria-hidden />} loading={exporting} onClick={() => void exportLearned()}>
                    Export
                  </Button>
                }
              />
              <SettingRow
                label={`Reset ${scopeMailbox ? `“${scopeLabel}”` : 'global'} learned data`}
                description={scopeMailbox ? 'Deletes the preferences, examples and corrections for this account only.' : 'Deletes the global preferences and examples. Account-specific scopes are kept.'}
                control={
                  <Button size="sm" variant="outline" icon={<RotateCcw className="h-4 w-4" aria-hidden />} loading={reset.isPending && !!reset.variables && 'mailboxId' in reset.variables} onClick={() => void askReset(false)}>
                    Reset {scopeMailbox ? 'this account' : 'global'}
                  </Button>
                }
              />
            </Rows>
          </CardBody>
        </Card>

        <DangerCard icon={<RotateCcw className="h-5 w-5" aria-hidden />} title="Reset everything" description="Erases the preferences, examples and corrections of every scope. Export first if you may want them back.">
          <CardBody className="flex justify-end !pt-5">
            <Button variant="danger" icon={<RotateCcw className="h-4 w-4" aria-hidden />} loading={reset.isPending && !!reset.variables && !('mailboxId' in reset.variables)} onClick={() => void askReset(true)}>
              Reset everything
            </Button>
          </CardBody>
        </DangerCard>
      </div>
    </div>
  );
}
